/**
 * routePrefetch — Map des routes → loader d'import dynamique.
 *
 * - Utilisé par `App.js` pour `React.lazy(loader)` (code-splitting).
 * - Utilisé par `Layout.jsx` pour précharger une page sur survol du lien
 *   sidebar via `onMouseEnter` / `onFocus`. Le navigateur et webpack mettent
 *   en cache le résultat de l'import, donc la navigation effective réutilise
 *   le chunk déjà téléchargé → ressenti "instantané".
 *
 * Important : ne PAS exposer les composants directement ici (ça les chargerait
 * eager). On expose juste les loaders (`() => import(...)`).
 */

export const routeLoaders = {
  '/dashboard': () => import('../pages/Dashboard'),
  '/mes-objectifs': () => import('../pages/MesObjectifs'),
  '/agence-map': () => import('../pages/AgenceObjectifMap'),
  '/mes-documents': () => import('../components/documents/MesDocuments'),
  '/secteurs': () => import('../pages/Secteurs'),
  '/contracts': () => import('../pages/ContractsList'),
  '/suivi-client': () => import('../pages/SuiviClient'),
  '/objectifs': () => import('../pages/Objectifs'),
  '/commission-dashboard': () => import('../pages/CommissionDashboard'),
  '/seller-space': () => import('../pages/SellerSpace'),
  '/admin': () => import('../pages/Admin'),
  '/outils': () => import('../pages/Outils'),
  '/infos': () => import('../pages/Infos'),
  '/formation': () => import('../pages/Training'),
  '/formation/octopus-energy': () => import('../pages/OctopusPresentation'),
  '/formation/ilek': () => import('../pages/IlekPresentation'),
  '/formation/quiz-octopus-vente': () => import('../pages/QuizOctopusVente'),
  '/organigramme': () => import('../pages/OrganigrammePage'),
  '/organisation': () => import('../pages/OrganisationAdmin'),
  '/voitures': () => import('../pages/Voitures'),
  '/fuel': () => import('../pages/FuelDashboard'),
  '/db-admin': () => import('../pages/DatabaseAdmin'),
  '/suivi-cartons': () => import('../pages/SuiviCartons'),
  '/chat': () => import('../pages/Chat'),
  '/quality-overview': () => import('../pages/QualityOverview'),
  '/alert-churn': () => import('../pages/AlertChurn'),
  '/rattrapage': () => import('../pages/Rattrapage'),
  '/ilek/dashboard': () => import('../pages/IlekDashboard'),
  '/ilek/contracts': () => import('../pages/IlekContracts'),
  '/ilek/qualite':   () => import('../pages/IlekQualite'),
  '/bouygues/dashboard': () => import('../pages/BouyguesDashboard'),
  '/bouygues/contracts': () => import('../pages/BouyguesContracts'),
  '/assistant-ia': () => import('../pages/AssistantIA'),
};

// Cache des promesses pour éviter de relancer un import déjà en cours/résolu.
const inflight = new Map();

export function prefetchRoute(path) {
  const loader = routeLoaders[path];
  if (!loader) return;
  if (inflight.has(path)) return;
  // On exécute le loader et on conserve la promesse pour mémoïser.
  // En cas d'erreur réseau, on retire l'entrée pour permettre un retry au prochain survol.
  const p = loader().catch((err) => {
    inflight.delete(path);
    throw err;
  });
  inflight.set(path, p);
}
